import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Building2, ChevronLeft, ChevronRight, ExternalLink, Home, Ruler, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1645510807290-cc82de2749f2?crop=entropy&cs=srgb&fm=jpg&q=85';
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If it's a relative path, prepend the backend URL
  return `${BACKEND_URL}${imagePath}`;
};

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextImage = () => {
    if (project?.images && project.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }
  };

  const prevImage = () => {
    if (project?.images && project.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };

  const openGoogleMaps = () => {
    if (project?.location) {
      const { lat, lng } = project.location;
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    } else if (project?.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.address)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="text-xl text-gray-600 mb-6">Project not found</div>
        <Link to="/portfolio">
          <Button>Back to Portfolio</Button>
        </Link>
      </div>
    );
  }

  // Get images array or fallback to single image
  const rawImages = project?.images || [project?.image_url || 'https://images.unsplash.com/photo-1645510807290-cc82de2749f2?crop=entropy&cs=srgb&fm=jpg&q=85'];
  const images = rawImages.map(img => getImageUrl(img));

  return (
    <div className="project-detail-page bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/portfolio">
            <Button variant="ghost" className="hover:bg-gray-100">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Portfolio
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Left Column (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Carousel */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl">
              <div className="relative h-[500px] group cursor-pointer" onClick={() => {
                setModalImageIndex(currentImageIndex);
                setIsImageModalOpen(true);
              }}>
                <img
                  src={images[currentImageIndex]}
                  alt={`${project.name} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover hover:opacity-95 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100 z-10"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100 z-10"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}

                {/* Status Badge */}
                {project.status && (
                  <div className="absolute top-4 left-4 px-4 py-2 bg-[#20B2AA] text-white text-sm font-medium rounded-full shadow-lg">
                    {project.status}
                  </div>
                )}
                
                {/* Featured Badge - only show if property is featured */}
                {project.featured && (
                  <div className="absolute top-4 right-4 px-4 py-2 bg-yellow-500 text-white text-sm font-semibold rounded-full shadow-lg flex items-center gap-1">
                    <span>⭐</span>
                    <span>Featured</span>
                  </div>
                )}
              </div>

              {/* Image Thumbnails */}
              {images.length > 1 && (
                <div className="p-4 flex gap-3 overflow-x-auto">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                        currentImageIndex === index 
                          ? 'border-[#20B2AA] scale-105' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Project Info Card */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h1 className="text-4xl font-bold text-black mb-4 whitespace-pre-line">{project.name}</h1>
              
              <div className="flex items-center gap-2 text-gray-600 mb-6">
                <MapPin className="h-5 w-5 text-[#20B2AA]" />
                <span className="text-lg">{project.address}</span>
              </div>

              {project.status && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#20B2AA]/10 text-[#20B2AA] rounded-full mb-6 font-medium">
                  <div className="w-2 h-2 bg-[#20B2AA] rounded-full animate-pulse" />
                  {project.status}
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {project.units && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 text-center hover:shadow-md transition-shadow duration-300">
                    <Home className="h-7 w-7 text-[#20B2AA] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-black mb-1">{project.units}</div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">Units</div>
                  </div>
                )}
                {project.square_feet && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 text-center hover:shadow-md transition-shadow duration-300">
                    <Ruler className="h-7 w-7 text-[#20B2AA] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-black mb-1">{project.square_feet}</div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">Sq. Feet</div>
                  </div>
                )}
                {project.year && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 text-center hover:shadow-md transition-shadow duration-300">
                    <Calendar className="h-7 w-7 text-[#20B2AA] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-black mb-1">{project.year}</div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">Year</div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h2 className="text-2xl font-bold mb-4 text-black">About This Project</h2>
                {project.description ? (
                  <div
                    className="rich-content"
                    dangerouslySetInnerHTML={{ __html: project.description }}
                  />
                ) : (
                  <p className="text-gray-600 leading-relaxed">
                    This premier development represents GN Management's commitment to creating high-quality, sustainable urban living spaces in Jersey City. Featuring modern design, premium amenities, and strategic location, this project exemplifies our vision for community-centric real estate development.
                  </p>
                )}
              </div>
            </div>

            {/* Map Section */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-black flex items-center gap-2">
                  <MapPin className="h-6 w-6 text-[#20B2AA]" />
                  Location
                </h2>
              </div>
              
              <div className="relative">
                {/* Google Maps Embed */}
                {project.location ? (
                  <iframe
                    title="Property Location"
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${project.location.lat},${project.location.lng}&zoom=15`}
                    allowFullScreen
                  />
                ) : (
                  <iframe
                    title="Property Location"
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(project.address)}&zoom=15`}
                    allowFullScreen
                  />
                )}
                
                {/* Get Directions Button Overlay */}
                <div className="p-6">
                  <Button
                    onClick={openGoogleMaps}
                    className="w-full bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-full py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    Get Directions in Google Maps
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Right Column (1/3) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Contact Card */}
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 text-white shadow-2xl">
                <h3 className="text-2xl font-bold mb-4">Interested in This Property?</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Contact us to learn more about investment opportunities, leasing information, or schedule a site visit.
                </p>
                <Link to="/contact">
                  <Button
                    data-testid="contact-about-project-btn"
                    size="lg"
                    className="w-full bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-full py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Get in Touch
                  </Button>
                </Link>
              </div>

              {/* Property Highlights */}
              {project.highlights && project.highlights.length > 0 && (
                <div className="bg-white rounded-3xl p-6 shadow-lg">
                  <h3 className="text-lg font-bold mb-4 text-black">Property Highlights</h3>
                  <ul className="space-y-3 text-sm text-gray-600">
                    {project.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-[#20B2AA] rounded-full mt-2 flex-shrink-0" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Amenities Card */}
              {project.amenities && project.amenities.length > 0 && (
                <div className="bg-white rounded-3xl p-6 shadow-lg">
                  <h3 className="text-lg font-bold mb-4 text-black">Key Amenities</h3>
                  <ul className="space-y-3 text-sm text-gray-600">
                    {project.amenities.map((amenity, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-[#20B2AA] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                        <span>{amenity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full-Size Image Modal */}
      {isImageModalOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <button
            onClick={() => setIsImageModalOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Close modal"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="relative max-w-7xl w-full h-full flex items-center justify-center">
            <img
              src={images[modalImageIndex]}
              alt={`${project.name} - Full size`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navigation Arrows for Modal */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalImageIndex((prev) => (prev - 1 + images.length) % images.length);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalImageIndex((prev) => (prev + 1) % images.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Image Counter in Modal */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm font-medium">
                  {modalImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
